// src/lib/pointage.ts
// Helpers partagés du pointage Présent / Absent d'un RDV.
import type { GoogleEvent, Prospect } from "@shared/schema";
import { addDaysIso, parisDateOf } from "@shared/followup";
import { api } from "./api";

export const TODAY_CHANGED_EVENT = "crm:today-changed";

/** Prévient les écrans qui affichent le bandeau / la file d'Aujourd'hui de se recalculer. */
export function notifyTodayChanged(): void {
  window.dispatchEvent(new Event(TODAY_CHANGED_EVENT));
}

export type PointageState = "upcoming" | "topoint" | "present" | "absent";

/**
 * État de pointage d'un RDV. Un RDV annulé n'est jamais passé à cette
 * fonction par les appelants : pas d'état « cancelled », on ne le gère pas ici.
 */
export function pointageState(
  event: Pick<GoogleEvent, "startTime" | "status" | "outcome">,
  now: Date = new Date(),
): PointageState {
  if (event.outcome === "attended") return "present";
  if (event.outcome === "no_show") return "absent";
  const start = new Date(event.startTime).getTime();
  if (Number.isNaN(start) || start > now.getTime()) return "upcoming";
  return "topoint";
}

/** Pointe un RDV rattaché à une fiche par l'API d'issue (un seul chemin d'écriture). */
export async function pointer(
  prospectId: number,
  eventId: number,
  kind: "present" | "no_show",
): Promise<{ prospect: Prospect; touchedProspect: boolean }> {
  const followUpAt =
    kind === "no_show"
      ? (addDaysIso(parisDateOf(new Date().toISOString()) ?? "", 1) ?? undefined)
      : undefined;
  const res = await api.setOutcome(prospectId, { choice: kind, eventId, followUpAt });
  notifyTodayChanged();
  return { prospect: res.prospect, touchedProspect: res.touchedProspect };
}

/** Annule le pointage d'un RDV (la relance posée par « Absent » reste en place). */
export async function annulerPointage(eventId: number): Promise<void> {
  await api.setEventOutcome(eventId, null);
  notifyTodayChanged();
}
